import type { GameState, Scene, Viewport } from "../sim/types";

/**
 * Fondo de espacio profundo. Tres capas: nebulosas, campo de estrellas con
 * parpadeo por estrella, y un hito por sector. Cada ola cruza suavemente de la
 * escenografia anterior a la nueva.
 */

function nebula(
  cx: CanvasRenderingContext2D,
  sc: Scene,
  alpha: number,
  now: number,
  vp: Viewport,
): void {
  for (const b of sc.blobs) {
    const x = b.x * vp.w;
    const y = b.y * vp.h + Math.sin(now * 0.00007 + b.h) * 8;
    const r = b.r * Math.max(vp.w, vp.h) * 0.6;
    const g = cx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `hsla(${b.h},72%,58%,${(b.a * alpha).toFixed(3)})`);
    g.addColorStop(0.55, `hsla(${b.h},70%,40%,${(b.a * 0.42 * alpha).toFixed(3)})`);
    g.addColorStop(1, `hsla(${b.h},70%,20%,0)`);
    cx.fillStyle = g;
    cx.fillRect(0, 0, vp.w, vp.h);
  }
}

function stars(
  cx: CanvasRenderingContext2D,
  sc: Scene,
  alpha: number,
  now: number,
  vp: Viewport,
  drift: number,
  frozen: boolean,
): void {
  for (const s of sc.stars) {
    const y = ((s.y * vp.h + (frozen ? 0 : drift * s.z * s.z * 0.9)) % (vp.h + 8)) - 4;
    const tw = 0.72 + 0.28 * Math.sin(now * 0.002 + s.tw);
    cx.fillStyle = `rgba(234,255,246,${((0.1 + s.z * 0.55) * tw * alpha).toFixed(3)})`;
    const r = 0.7 + s.z * 1.7;
    cx.fillRect(s.x * vp.w, y, r, r);
  }
}

function feature(
  cx: CanvasRenderingContext2D,
  sc: Scene,
  alpha: number,
  now: number,
  vp: Viewport,
): void {
  const x = sc.fx * vp.w;
  const y = sc.fy * vp.h;
  const s0 = sc.fs;
  cx.save();
  cx.globalAlpha = alpha;

  if (sc.feature === "spiral") {
    const R = 90 * s0;
    for (let i = 0; i < 420; i++) {
      const t = i / 420;
      const arm = (i % 2) * Math.PI;
      const th = t * 5.4 + arm + sc.frot + now * 0.00002;
      const rad = R * Math.pow(t, 0.62);
      const px = x + Math.cos(th) * rad;
      const py = y + Math.sin(th) * rad * 0.42;
      cx.fillStyle = `hsla(${sc.hue},70%,${64 - t * 22}%,${(0.32 * (1 - t * 0.7)).toFixed(3)})`;
      cx.fillRect(px, py, 1.4, 1.4);
    }
    const g = cx.createRadialGradient(x, y, 0, x, y, R * 0.28);
    g.addColorStop(0, `hsla(${sc.hue},60%,86%,0.34)`);
    g.addColorStop(1, `hsla(${sc.hue},60%,60%,0)`);
    cx.fillStyle = g;
    cx.fillRect(x - R, y - R, R * 2, R * 2);
  } else if (sc.feature === "giant") {
    const R = 78 * s0;
    const g = cx.createRadialGradient(x - R * 0.4, y - R * 0.4, R * 0.1, x, y, R);
    g.addColorStop(0, `hsla(${sc.hue},56%,44%,0.5)`);
    g.addColorStop(0.7, `hsla(${sc.hue2},52%,22%,0.42)`);
    g.addColorStop(1, `hsla(${sc.hue2},50%,10%,0.06)`);
    cx.fillStyle = g;
    cx.beginPath();
    cx.arc(x, y, R, 0, 6.283);
    cx.fill();

    cx.save();
    cx.beginPath();
    cx.arc(x, y, R, 0, 6.283);
    cx.clip();
    for (let i = 0; i < 7; i++) {
      const by = y - R + (i + 0.5) * ((R * 2) / 7);
      cx.fillStyle = `hsla(${(sc.hue + i * 9) % 360},58%,${30 + i * 4}%,0.16)`;
      cx.fillRect(x - R, by - R * 0.07, R * 2, R * 0.14);
    }
    cx.restore();

    cx.strokeStyle = `hsla(${sc.hue},60%,70%,0.20)`;
    cx.lineWidth = 1.2;
    cx.beginPath();
    cx.ellipse(x, y, R * 1.7, R * 0.34, -0.34, 0, 6.283);
    cx.stroke();
  } else if (sc.feature === "dust") {
    for (let i = 0; i < 9; i++) {
      const yy = y + i * 22 * s0;
      const a = 0.05 + 0.05 * Math.sin(i + sc.frot);
      const g = cx.createLinearGradient(0, yy, vp.w, yy + 40);
      g.addColorStop(0, `hsla(${sc.hue},60%,50%,0)`);
      g.addColorStop(0.5, `hsla(${sc.hue},66%,54%,${a.toFixed(3)})`);
      g.addColorStop(1, `hsla(${sc.hue2},60%,50%,0)`);
      cx.fillStyle = g;
      cx.fillRect(0, yy, vp.w, 26 * s0);
    }
  } else {
    for (let i = 0; i < 46; i++) {
      const t = (i / 46 + now * 0.00006) % 1;
      const px = (sc.fx + Math.sin(i * 2.3) * 0.42) * vp.w;
      const py = t * vp.h;
      cx.strokeStyle = `hsla(${sc.hue},80%,68%,${(0.16 * (1 - Math.abs(t - 0.5) * 1.6)).toFixed(3)})`;
      cx.lineWidth = 1;
      cx.beginPath();
      cx.moveTo(px, py);
      cx.lineTo(px + Math.sin(i) * 7, py + 26 * s0);
      cx.stroke();
    }
  }
  cx.restore();
}

export function drawScenery(
  cx: CanvasRenderingContext2D,
  s: GameState,
  now: number,
  vp: Viewport,
): void {
  const frozen = s.freeze > 0;
  if (s.prevScene && s.fade < 1) {
    const a = 1 - s.fade;
    nebula(cx, s.prevScene, a, now, vp);
    stars(cx, s.prevScene, a, now, vp, s.drift, frozen);
    feature(cx, s.prevScene, a * 0.9, now, vp);
  }
  const a = s.prevScene ? s.fade : 1;
  nebula(cx, s.scene, a, now, vp);
  stars(cx, s.scene, a, now, vp, s.drift, frozen);
  feature(cx, s.scene, a * 0.9, now, vp);
}
